import * as React from "react";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import {ChangeUpgradeStrategyRequest} from "client/resources/changeUpgradeStrategyRequest";
import {EnvironmentName} from "client/resources/environmentName";
import {
    isSpecifiedVersionUpgradeStrategyType,
    TrackBranchUpgradeStrategy,
    UpgradeStrategyType,
    upgradeStrategyTypeDescriptions,
    SpecifiedVersionUpgradeStrategy,
    selectableUpgradeStrategyTypes
} from "client/resources/upgradeStrategy";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Select, Text} from "components/form";
import {ChangeUpgradeStrategyCallout} from "./ChangeUpgradeStrategyCallout";
import repository from "client/repository";
import {cloneDeep} from "lodash";

interface ChangeUpgradeStrategyDialogProps {
    instance: HostedInstanceResource;
    onSaved: (updatedInstance: HostedInstanceResource) => void;
}

interface ChangeUpgradeStrategyDialogState extends DataBaseComponentState {
    changeUpgradeStrategyRequest: Partial<ChangeUpgradeStrategyRequest>;
    environment?: EnvironmentName;
}

export class ChangeUpgradeStrategyDialog extends DataBaseComponent<ChangeUpgradeStrategyDialogProps, ChangeUpgradeStrategyDialogState> {
    constructor(props: ChangeUpgradeStrategyDialogProps) {
        super(props);
        this.state = {
            changeUpgradeStrategyRequest: {
                UpgradeStrategy: cloneDeep(props.instance.UpgradeStrategy)
            }
        };
    }

    async componentDidMount() {
        await this.doBusyTask(async () => {
            this.setState({environment: (await repository.Configuration.getAppInfo()).Environment});
        });
    }

    render() {
        const request = this.state.changeUpgradeStrategyRequest;

        return <SaveDialogLayout title={"Change Upgrade Strategy"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.changeUpgradeStrategy(request)}>

            <ChangeUpgradeStrategyCallout />

            {this.state.environment !== undefined &&
                <Select value={request.UpgradeStrategy.UpgradeStrategyType}
                        onChange={(upgradeStrategyType: UpgradeStrategyType) => this.updateUpgradeStrategy({UpgradeStrategy: {UpgradeStrategyType: upgradeStrategyType}})}
                        items={selectableUpgradeStrategyTypes(this.state.environment).map(t => ({value: t, text: upgradeStrategyTypeDescriptions[t]}))}
                        fieldName={"upgrade strategy"}
                />
            }

            {request.UpgradeStrategy && request.UpgradeStrategy.UpgradeStrategyType === UpgradeStrategyType.TrackBranchUpgradeStrategy &&
                <Text label="Branch"
                      hintText="Branch to track, eg main"
                      value={(request.UpgradeStrategy as TrackBranchUpgradeStrategy).BranchName}
                      onChange={(branchName: string) => this.updateUpgradeStrategy({
                          UpgradeStrategy: {
                              UpgradeStrategyType: UpgradeStrategyType.TrackBranchUpgradeStrategy,
                              BranchName: branchName
                          }
                      })}
                      autoFocus={true}
                />
            }

            {request.UpgradeStrategy && isSpecifiedVersionUpgradeStrategyType(request.UpgradeStrategy.UpgradeStrategyType) &&
                <Text label="Initial Version"
                      hintText="Defaults to the Instance's current version; cannot be changed"
                      disabled={true}
                      value={(request.UpgradeStrategy as SpecifiedVersionUpgradeStrategy).Version}
                      onChange={(version: string) => this.updateUpgradeStrategy({
                          UpgradeStrategy: {
                              UpgradeStrategyType: request.UpgradeStrategy.UpgradeStrategyType,
                              Version: version
                          } as SpecifiedVersionUpgradeStrategy
                      })}
                />
            }
        </SaveDialogLayout>;
    }

    private updateUpgradeStrategy(update: Partial<ChangeUpgradeStrategyRequest>) {
        const updatePartial = {...update};
        if (isSpecifiedVersionUpgradeStrategyType(update.UpgradeStrategy.UpgradeStrategyType)) {
            (updatePartial.UpgradeStrategy as SpecifiedVersionUpgradeStrategy).Version = this.props.instance.Version;
        }
        return this.setState(prevState => ({changeUpgradeStrategyRequest: {...prevState.changeUpgradeStrategyRequest, ...updatePartial}}));
    }

    private changeUpgradeStrategy(request: Partial<ChangeUpgradeStrategyRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.changeUpgradeStrategy(this.props.instance, request as ChangeUpgradeStrategyRequest);
            this.props.onSaved(instance);
            return true;
        });
    }
}
